import { useWeb3Modal } from "@web3modal/react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAccount, useDisconnect, useSignMessage } from "wagmi";
import { ApiPaths } from "../../api/paths";
import useDisclosure from "../../hooks/use-disclosure";
import useMutateData from "../../hooks/use-mutate-data";
import { axiosInstance } from "../../services/axios-config/axios-config";
import { PaginatedApiResponseType } from "../../types/common";
import { WalletType } from "../../types/user";
import { setTokens } from "../../utils/token-functions";
import ConfirmSignature from "./ConfirmSignature";

interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  isLogin?: boolean;
  children: React.ReactNode;
}

export default function WalletConnectButton({
  children,
  isLogin = false,
  ...rest
}: Props) {
  const { t } = useTranslation(["common"]);
  const { open } = useWeb3Modal();
  const [walletAddress, setWalletAddress] = useState("");
  const getWallets = async () => {
    const result = await axiosInstance.get(ApiPaths.wallets);
    return result.data;
  };
  const { mutate: addWallet } = useMutateData(ApiPaths.wallets, {
    method: "POST",
  });
  const { disconnect } = useDisconnect();
  const { data: signedData, signMessage, isError } = useSignMessage();

  if (isError) {
    disconnect();
  }
  const { onHide, onShow, show } = useDisclosure();
  const { data, mutate: getNonce } = useMutateData(
    `${ApiPaths.wallets}/${walletAddress}/nonce`,
    {
      method: "GET",
    }
  );
  const { mutate: getTokens } = useMutateData(`${ApiPaths.loginUser}`, {
    options: {
      headers: {
        walletAddress,
        nonceSignature: signedData ?? "",
      },
    },
    onSuccess: async (getTokensData) => {
      if (getTokensData?.access_token && getTokensData?.refresh_token) {
        const walletList: PaginatedApiResponseType<WalletType[]> =
          await getWallets();
        if (
          walletAddress &&
          !walletList?.data.map((w) => w.walletId).includes(walletAddress)
        ) {
          addWallet(walletAddress);
        }
        setTokens(getTokensData.access_token, getTokensData.refresh_token);
        window.location.href = window.location.origin + "/dashboard";
      } else {
        toast(t("error.wallet_login", { ns: "common" }), { type: "error" });
        disconnect();
        window.location.reload();
      }
    },
    onError: () => {
      toast(t("error.wallet_login", { ns: "common" }), { type: "error" });
      disconnect();
      window.location.reload();
    },
    method: "GET",
  });

  const { address, isConnected, isDisconnected } = useAccount();

  // useAccount({
  //   onConnect: async ({ address }) => {
  //     setWalletAddress(address ?? "");
  //     if (isLogin) {
  //       getNonce(undefined, `${ApiPaths.wallets}/${address}/nonce`);
  //       return;
  //     }
  //     const walletList: PaginatedApiResponseType<WalletType[]> =
  //       await getWallets();
  //     if (
  //       !isLogin &&
  //       !walletList?.data?.map((w) => w.walletId).includes(address ?? "")
  //     ) {
  //       addWallet(address);
  //     }
  //   },
  //   onDisconnect: () => {
  //     setWalletAddress("");
  //     onHide();
  //   },
  // });
  useEffect(() => {
    if (data) {
      signMessage({ message: data?.nonce });
      onShow();
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!address) {
        setWalletAddress(address ?? "");
      }
      if (!isConnected) {
        if (isLogin) {
          await getNonce(undefined, `${ApiPaths.wallets}/${address}/nonce`);
          return;
        }
        const walletList = await getWallets();
        if (
          !isLogin &&
          !walletList?.data?.map((w: any) => w.walletId).includes(address ?? "")
        ) {
          addWallet(address);
        }
      }
      if (!isDisconnected) {
        setWalletAddress("");
        onHide();
      }
    };

    fetchData();
  }, [address, isConnected, isDisconnected, isLogin]);

  useEffect(() => {
    if (walletAddress && data && signedData) {
      getTokens();
    }
  }, [signedData, data, walletAddress]);

  const onOpen = async () => {
    await open();
  };
  return (
    <>
      <ConfirmSignature show={show} onHide={onHide} />
      <button onClick={onOpen} type="button" {...rest}>
        {walletAddress ? t("button.view_wallet", { ns: "common" }) : children}
      </button>
    </>
  );
}
